import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Button,
    Checkbox,
    List,
    ListItem,
    ListItemText,
    Popover,
    Typography,
    Dialog,
    DialogTitle,
    DialogContent,
    IconButton,
} from '@material-ui/core';
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown';
import FilterListIcon from '@material-ui/icons/FilterList';
import WarningIcon from '@material-ui/icons/Warning';
import CloseIcon from '@material-ui/icons/Close';
import Portlet from 'components/Portlet';
import PortletHeader from 'components/PortletHeader';
import PortletLabel from 'components/PortletLabel';
import PortletToolbar from 'components/PortletToolbar';
import PortletContent from 'components/PortletContent';
import PortletFooter from 'components/PortletFooter';
import { getCallMetrics } from 'services/getCallMetrics';
import { getAudioList } from 'services/getAudioList';

import { PERIODS } from './constants';
import { transformApiResponse } from './transformer';
import {
    formatTzShort,
    formatTzFull,
    normalizeMos,
    calcAvg,
    calcP95,
    getMosQuality,
    getJitterColor,
    getJitterQuality,
    getPacketLossColor,
    getPacketLossQuality,
    getMesQuality,
} from './helpers';
import { buildSparklineOptions } from './chartOptions';
import AudioStepPlayer from './AudioStepPlayer';
import MetricCard from './MetricCard';

export default function CallMetrics({ objetivoId, userId }) {
    const [periodAnchorEl, setPeriodAnchorEl] = useState(null);
    const [period, setPeriod] = useState('d');
    const [nodoAnchorEl, setNodoAnchorEl] = useState(null);
    const [selectedNodos, setSelectedNodos] = useState([]);
    const [apiData, setApiData] = useState([]);
    const [allNodos, setAllNodos] = useState([]);
    const [loading, setLoading] = useState(false);
    const [audioModalOpen, setAudioModalOpen] = useState(false);
    const [selectedAudio, setSelectedAudio] = useState(null);
    const [audioListLoading, setAudioListLoading] = useState(false);
    const { t } = useTranslation();

    // Fetch data from API
    useEffect(() => {
        if (!objetivoId) return;
        setLoading(true);
        const token = sessionStorage.getItem('key');
        getCallMetrics(objetivoId, period, userId, token)
            .then(response => {
                const transformed = transformApiResponse(response);
                setApiData(transformed);
                // Extrae todos los nodos de la respuesta, incluso los sin métricas
                const nodos = (response && response.Nodos || [])
                    .map(n => n.nombre)
                    .filter(Boolean)
                    .sort();
                setAllNodos([...new Set(nodos)]);
            })
            .catch(() => { setApiData([]); setAllNodos([]); })
            .finally(() => setLoading(false));
    }, [objetivoId, userId, period]);

    // Nodos que tienen datos (para marcar en el filtro)
    const nodosWithData = useMemo(() => {
        return new Set(apiData.map(d => d.nodo).filter(Boolean));
    }, [apiData]);

    // Nodos disponibles (todos, incluso sin métricas)
    const availableNodos = allNodos;

    // Filtrar datos por nodo
    const rawData = useMemo(() => {
        if (selectedNodos.length === 0) return apiData;
        return apiData.filter(d => selectedNodos.includes(d.nodo));
    }, [apiData, selectedNodos]);

    // Nodo handlers
    const handleNodoOpen = (e) => setNodoAnchorEl(e.currentTarget);
    const handleNodoClose = () => setNodoAnchorEl(null);
    const handleNodoToggle = (nodo) => {
        setSelectedNodos(prev =>
            prev.includes(nodo) ? prev.filter(n => n !== nodo) : [...prev, nodo]
        );
    };
    const handleNodoSelectAll = () => setSelectedNodos([]);

    const handlePointClick = useCallback((pointData) => {
        if (!pointData) return;
        setSelectedAudio({ ...pointData, audios: null });
        setAudioModalOpen(true);
        setAudioListLoading(true);

        // Calcular ventana de tiempo: ±5 min alrededor del punto
        const pointDate = new Date(pointData.timestamp);
        const begin = new Date(pointDate.getTime() - 5 * 60 * 1000);
        const end = new Date(pointDate.getTime() + 5 * 60 * 1000);
        const fmt = d => d.toISOString().slice(0, 19).replace('T', ' ');

        const token = sessionStorage.getItem('key');
        const monitorId = pointData.monitorId;

        if (!monitorId || !objetivoId) {
            setAudioListLoading(false);
            setSelectedAudio(prev => prev ? { ...prev, audios: [] } : prev);
            return;
        }

        getAudioList(objetivoId, fmt(begin), fmt(end), token, monitorId)
            .then(audioItems => {
                const audios = Array.isArray(audioItems) ? audioItems : [];
                setSelectedAudio(prev => prev ? { ...prev, audios } : prev);
            })
            .catch(() => {
                setSelectedAudio(prev => prev ? { ...prev, audios: [] } : prev);
            })
            .finally(() => setAudioListLoading(false));
    }, [objetivoId]);

    const handleCloseAudioModal = useCallback(() => {
        setAudioModalOpen(false);
        setSelectedAudio(null);
    }, []);

    const nodoButtonLabel = selectedNodos.length === 0
        ? t('cqp.filter.all-nodos')
        : selectedNodos.length === 1
            ? selectedNodos[0]
            : `${selectedNodos.length} ${t('cqp.filter.nodos')}`;

    // Cálculos memorizados
    const metrics = useMemo(() => {
        // MOS: rxmes/txmes escala 0-100, normalizeMos lo convierte a 1-5
        const mosValues = rawData.map(d => {
            const rx = d.rxmes;
            const tx = d.txmes;
            if (rx != null && rx > 0 && tx != null && tx > 0) return normalizeMos((rx + tx) / 2);
            if (tx != null && tx > 0) return normalizeMos(tx);
            if (rx != null && rx > 0) return normalizeMos(rx);
            return null;
        });
        // MES: txmos ya en escala 1-5
        const mesValues = rawData.map(d => d.txmos);
        // Jitter: ya convertido a ms en el transformer
        const jitterValues = rawData.map(d => d.jitter);
        // LP: suma paquetes perdidos locales + remotos
        const lpValues = rawData.map(d => {
            const local = d.packetLoss ?? 0;
            const remote = d.rlp ?? 0;
            return local + remote;
        });
        const categories = rawData.map(d => formatTzShort(d.timestamp));

        return {
            mos: { values: mosValues, avg: calcAvg(mosValues), p95: calcP95(mosValues) },
            mes: { values: mesValues, avg: calcAvg(mesValues), p95: calcP95(mesValues) },
            jitter: { values: jitterValues, avg: calcAvg(jitterValues), p95: calcP95(jitterValues) },
            lp: { values: lpValues, avg: calcAvg(lpValues), p95: calcP95(lpValues) },
            categories,
        };
    }, [rawData]);

    const mosQuality = getMosQuality(metrics.mos.avg, t);
    const isDegraded = metrics.mos.avg < 3.5;

    const handlePeriodOpen = (event) => setPeriodAnchorEl(event.currentTarget);
    const handlePeriodClose = () => setPeriodAnchorEl(null);
    const handlePeriodSelect = (value) => {
        setPeriod(value);
        handlePeriodClose();
    };
    const currentPeriodLabel = PERIODS.find(p => p.value === period)?.label || 'period.text.last-day';

    // Opciones de charts
    const mosChartOpts = buildSparklineOptions(metrics.mos.values, metrics.categories, {
        yMin: 1, yMax: 5,
        color: mosQuality.color,
        tooltipSuffix: '',
        plotLines: [
            { value: 4.0, color: '#FFC600', width: 1, dashStyle: 'Dash', label: { text: '4.0', style: { fontSize: '9px', color: '#FFC600' } } },
            { value: 3.5, color: '#C2272D', width: 1, dashStyle: 'Dash', label: { text: '3.5', style: { fontSize: '9px', color: '#C2272D' } } },
        ],
        t, rawData, onPointClick: handlePointClick,
    });

    const mesChartOpts = buildSparklineOptions(metrics.mes.values, metrics.categories, {
        yMin: 0, yMax: 5,
        color: '#1976d2',
        tooltipSuffix: '',
        plotLines: [
            { value: 4.0, color: '#FFC600', width: 1, dashStyle: 'Dash', label: { text: '4.0', style: { fontSize: '9px', color: '#FFC600' } } },
            { value: 3.5, color: '#C2272D', width: 1, dashStyle: 'Dash', label: { text: '3.5', style: { fontSize: '9px', color: '#C2272D' } } },
        ],
        t, rawData, onPointClick: handlePointClick,
    });

    const jitterChartOpts = buildSparklineOptions(metrics.jitter.values, metrics.categories, {
        yMin: 0,
        yMax: Math.max(35, ...metrics.jitter.values.filter(v => v != null)),
        color: getJitterColor(metrics.jitter.avg),
        tooltipSuffix: 'ms',
        plotLines: [
            { value: 20, color: '#FFC600', width: 1, dashStyle: 'Dash', label: { text: '20', style: { fontSize: '9px', color: '#FFC600' } } },
            { value: 30, color: '#C2272D', width: 1, dashStyle: 'Dash', label: { text: '30', style: { fontSize: '9px', color: '#C2272D' } } },
        ],
        t, rawData, onPointClick: handlePointClick,
    });

    const lpChartOpts = buildSparklineOptions(metrics.lp.values, metrics.categories, {
        yMin: 0,
        yMax: Math.max(5, ...metrics.lp.values.filter(v => v != null)),
        color: getPacketLossColor(metrics.lp.avg),
        tooltipSuffix: 'pkt',
        plotLines: [
            { value: 1, color: '#FFC600', width: 1, dashStyle: 'Dash', label: { text: '1', style: { fontSize: '9px', color: '#FFC600' } } },
            { value: 3, color: '#C2272D', width: 1, dashStyle: 'Dash', label: { text: '3', style: { fontSize: '9px', color: '#C2272D' } } },
        ],
        t, rawData, onPointClick: handlePointClick,
    });

    // Loading state
    if (loading) {
        return (
            <Portlet>
                <PortletHeader>
                    <PortletLabel title={t('cqp.title')} />
                </PortletHeader>
                <PortletContent>
                    <Typography variant="body1" style={{ color: '#6b6b6b', textAlign: 'center', padding: '40px 0' }}>
                        {t('cqp.loading')}
                    </Typography>
                </PortletContent>
            </Portlet>
        );
    }

    // Empty state
    if (!rawData || rawData.length === 0) {
        return (
            <Portlet>
                <PortletHeader>
                    <PortletLabel title={t('cqp.title')} />
                    {availableNodos.length > 0 && (
                        <PortletToolbar>
                            <Button
                                variant="outlined"
                                size="small"
                                onClick={handleNodoOpen}
                                startIcon={<FilterListIcon style={{ fontSize: 18 }} />}
                                style={{ textTransform: 'none', fontSize: 13, borderColor: '#ccc', color: '#555' }}
                            >
                                {t('cqp.filter.nodo')}: {nodoButtonLabel} <ArrowDropDownIcon style={{ fontSize: 18 }} />
                            </Button>
                            <Popover
                                anchorEl={nodoAnchorEl}
                                open={Boolean(nodoAnchorEl)}
                                onClose={handleNodoClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                                <List dense style={{ minWidth: 180 }}>
                                    <ListItem button onClick={handleNodoSelectAll} selected={selectedNodos.length === 0}>
                                        <Checkbox
                                            checked={selectedNodos.length === 0}
                                            size="small"
                                            style={{ padding: 4, marginRight: 4 }}
                                        />
                                        <ListItemText primary={t('cqp.filter.all-nodos')} />
                                    </ListItem>
                                    {availableNodos.map(nodo => {
                                        const hasData = nodosWithData.has(nodo);
                                        return (
                                            <ListItem key={nodo} button onClick={() => handleNodoToggle(nodo)}>
                                                <Checkbox
                                                    checked={selectedNodos.includes(nodo)}
                                                    size="small"
                                                    style={{ padding: 4, marginRight: 4 }}
                                                />
                                                <ListItemText
                                                    primary={nodo}
                                                    secondary={!hasData ? t('cqp.filter.no-data') : null}
                                                    secondaryTypographyProps={{ style: { fontSize: 11, color: '#999' } }}
                                                />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Popover>
                        </PortletToolbar>
                    )}
                </PortletHeader>
                <PortletContent>
                    <Typography variant="body1" style={{ color: '#6b6b6b', textAlign: 'center', padding: '40px 0' }}>
                        {t('cqp.empty')}
                    </Typography>
                </PortletContent>
                <PortletFooter>
                    <Button
                        style={{ fontSize: '15px', float: 'left', color: '#66788A' }}
                        onClick={handlePeriodOpen}
                    >
                        {t(currentPeriodLabel)} <ArrowDropDownIcon />
                    </Button>
                    <Popover
                        anchorEl={periodAnchorEl}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                        open={Boolean(periodAnchorEl)}
                        onClose={handlePeriodClose}
                    >
                        <List>
                            {PERIODS.map((p) => (
                                <ListItem key={p.value} button onClick={() => handlePeriodSelect(p.value)}>
                                    <Button>{t(p.label)}</Button>
                                </ListItem>
                            ))}
                        </List>
                    </Popover>
                </PortletFooter>
            </Portlet>
        );
    }

    return (
        <Portlet>
            <PortletHeader>
                <PortletLabel title={t('cqp.title')} />
                <PortletToolbar>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={handleNodoOpen}
                        startIcon={<FilterListIcon style={{ fontSize: 18 }} />}
                        style={{ textTransform: 'none', fontSize: 13, borderColor: '#ccc', color: '#555' }}
                    >
                        {t('cqp.filter.nodo')}: {nodoButtonLabel} <ArrowDropDownIcon style={{ fontSize: 18 }} />
                    </Button>
                    <Popover
                        anchorEl={nodoAnchorEl}
                        open={Boolean(nodoAnchorEl)}
                        onClose={handleNodoClose}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    >
                        <List dense style={{ minWidth: 180 }}>
                            <ListItem button onClick={handleNodoSelectAll} selected={selectedNodos.length === 0}>
                                <Checkbox
                                    checked={selectedNodos.length === 0}
                                    size="small"
                                    style={{ padding: 4, marginRight: 4 }}
                                />
                                <ListItemText primary={t('cqp.filter.all-nodos')} />
                            </ListItem>
                            {availableNodos.map(nodo => {
                                const hasData = nodosWithData.has(nodo);
                                return (
                                    <ListItem key={nodo} button onClick={() => handleNodoToggle(nodo)}>
                                        <Checkbox
                                            checked={selectedNodos.includes(nodo)}
                                            size="small"
                                            style={{ padding: 4, marginRight: 4 }}
                                        />
                                        <ListItemText
                                            primary={nodo}
                                            secondary={!hasData ? t('cqp.filter.no-data') : null}
                                            secondaryTypographyProps={{ style: { fontSize: 11, color: '#999' } }}
                                        />
                                    </ListItem>
                                );
                            })}
                        </List>
                    </Popover>
                </PortletToolbar>
            </PortletHeader>
            <PortletContent>
                <Box style={{ borderRadius: 8}}>
                    {/* Alerta de degradación */}
                    {isDegraded && (
                        <Box display="flex" alignItems="center" style={{
                            backgroundColor: '#FFF3CD',
                            border: '1px solid #FFC107',
                            borderRadius: 6,
                            padding: '4px 8px',
                            marginBottom: 8,
                        }}>
                            <WarningIcon style={{ color: '#FF7E00', marginRight: 8 }} />
                            <Box>
                                <Typography variant="body2" style={{ fontWeight: 600, color: '#856404' }}>
                                    {t('cqp.degraded-warning')}
                                </Typography>
                                <Typography variant="caption" style={{ color: '#856404' }}>
                                    {metrics.lp.avg > 1
                                        ? t('cqp.cause.packet-loss')
                                        : metrics.jitter.avg > 20
                                            ? t('cqp.cause.jitter')
                                            : t('cqp.cause.general')}
                                </Typography>
                            </Box>
                        </Box>
                    )}

                    {/* MOS */}
                    <MetricCard
                        title={`MOS (${t('cqp.mos.help')})`}
                        chartOptions={mosChartOpts}
                        avg={metrics.mos.avg.toFixed(2)}
                        p95={metrics.mos.p95.toFixed(2)}
                        avgColor={getMosQuality(metrics.mos.avg, t).color}
                        p95Color={getMosQuality(metrics.mos.p95, t).color}
                        unit=""
                        helpText={mosQuality.label}
                        helpTextColor={mosQuality.color}
                        tooltipText={t('cqp.tooltip.mos')}
                    />

                    {/* MES */}
                    <MetricCard
                        title={`MES (${t('cqp.mes.help')})`}
                        chartOptions={mesChartOpts}
                        avg={metrics.mes.avg.toFixed(2)}
                        p95={metrics.mes.p95.toFixed(2)}
                        avgColor={metrics.mes.avg >= 3.5 ? '#41B900' : '#C2272D'}
                        p95Color={metrics.mes.p95 >= 3.5 ? '#41B900' : '#C2272D'}
                        unit=""
                        helpText={getMesQuality(metrics.mes.avg, t).label}
                        helpTextColor={getMesQuality(metrics.mes.avg, t).color}
                        tooltipText={t('cqp.tooltip.mes')}
                    />

                    {/* Jitter */}
                    <MetricCard
                        title={`Jitter (${t('cqp.jitter.help')})`}
                        chartOptions={jitterChartOpts}
                        avg={metrics.jitter.avg.toFixed(0)}
                        p95={metrics.jitter.p95.toFixed(0)}
                        avgColor={getJitterColor(metrics.jitter.avg)}
                        p95Color={getJitterColor(metrics.jitter.p95)}
                        unit="ms"
                        helpText={getJitterQuality(metrics.jitter.avg, t).label}
                        helpTextColor={getJitterQuality(metrics.jitter.avg, t).color}
                        tooltipText={t('cqp.tooltip.jitter')}
                    />

                    {/* LP (Packet Loss) */}
                    <MetricCard
                        title={`LP (${t('cqp.lp.help')})`}
                        chartOptions={lpChartOpts}
                        avg={metrics.lp.avg.toFixed(1)}
                        p95={metrics.lp.p95.toFixed(1)}
                        avgColor={getPacketLossColor(metrics.lp.avg)}
                        p95Color={getPacketLossColor(metrics.lp.p95)}
                        unit="pkt"
                        helpText={getPacketLossQuality(metrics.lp.avg, t).label}
                        helpTextColor={getPacketLossQuality(metrics.lp.avg, t).color}
                        tooltipText={t('cqp.tooltip.lp')}
                    />
                </Box>
            </PortletContent>
            <PortletFooter>
                <Button
                    style={{ fontSize: '15px', float: 'left', color: '#66788A' }}
                    onClick={handlePeriodOpen}
                >
                    {t(currentPeriodLabel)} <ArrowDropDownIcon />
                </Button>
                <Popover
                    anchorEl={periodAnchorEl}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'center' }}
                    open={Boolean(periodAnchorEl)}
                    onClose={handlePeriodClose}
                >
                    <List>
                        {PERIODS.map((p) => (
                            <ListItem key={p.value} button onClick={() => handlePeriodSelect(p.value)}>
                                <Button>{t(p.label)}</Button>
                            </ListItem>
                        ))}
                    </List>
                </Popover>
            </PortletFooter>

            {/* Modal de audio */}
            <Dialog
                open={audioModalOpen}
                onClose={handleCloseAudioModal}
                maxWidth="sm"
                fullWidth
            >
                {selectedAudio && (
                    <>
                        <DialogTitle className="at-blue-background" disableTypography>
                            <Box display="flex" flexDirection="row" justifyContent="space-between" alignItems="center">
                                <Typography variant="h5" style={{ color: 'white', fontWeight: 600 }}>
                                    {t('cqp.audio.modal-title')}
                                </Typography>
                                <IconButton style={{ color: 'white' }} onClick={handleCloseAudioModal}>
                                    <CloseIcon />
                                </IconButton>
                            </Box>
                            <Typography variant="body2" style={{ color: 'rgba(255,255,255,0.8)', marginTop: 4 }}>
                                {selectedAudio.nodo} — {selectedAudio.timestamp ? formatTzFull(selectedAudio.timestamp) : ''}
                            </Typography>
                        </DialogTitle>
                        <DialogContent style={{ padding: 16 }}>
                            <Box mb={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 6, padding: 10 }}>
                                <Typography variant="body2" style={{ color: '#555' }}>
                                    <strong>MOS:</strong> {(() => {
                                        const rx = selectedAudio.rxmes;
                                        const tx = selectedAudio.txmes;
                                        if (rx != null && rx > 0 && tx != null && tx > 0) return normalizeMos((rx + tx) / 2).toFixed(2);
                                        if (tx != null && tx > 0) return normalizeMos(tx).toFixed(2);
                                        if (rx != null && rx > 0) return normalizeMos(rx).toFixed(2);
                                        return '—';
                                    })()}
                                    {' | '}
                                    <strong>Jitter:</strong> {selectedAudio.jitter != null ? `${selectedAudio.jitter.toFixed(1)} ms` : '—'}
                                    {' | '}
                                    <strong>LP:</strong> {(selectedAudio.packetLoss ?? 0) + (selectedAudio.rlp ?? 0)} pkt
                                    {' | '}
                                    <strong>RTT:</strong> {selectedAudio.rtt != null ? `${selectedAudio.rtt.toFixed(1)} ms` : '—'}
                                </Typography>
                            </Box>
                            <Typography variant="subtitle2" style={{ fontWeight: 600, marginBottom: 8, color: '#353535' }}>
                                {t('cqp.audio.steps-title')} {selectedAudio.audios ? `(${selectedAudio.audios.length})` : ''}
                            </Typography>
                            {audioListLoading
                                ? (
                                    <Typography variant="body2" style={{ color: '#999', textAlign: 'center', padding: '16px 0' }}>
                                        {t('cqp.audio.loading')}
                                    </Typography>
                                )
                                : selectedAudio.audios && selectedAudio.audios.length > 0
                                    ? selectedAudio.audios.map((audio, idx) => (
                                        <AudioStepPlayer key={audio.hash_md5 || idx} audio={audio} t={t} objetivoId={objetivoId} />
                                    ))
                                    : (
                                        <Typography variant="body2" style={{ color: '#999', fontStyle: 'italic', textAlign: 'center', padding: '16px 0' }}>
                                            {t('cqp.audio.no-audios')}
                                        </Typography>
                                    )
                            }
                        </DialogContent>
                    </>
                )}
            </Dialog>
        </Portlet>
    );
}
